/**
 * Доступ к публичному контуру API.
 *
 * Типы описаны здесь, а не импортированы из бэкенда: гостевая витрина
 * собирается отдельно и знать про Prisma не должна. Взамен формы ответов
 * держатся в одном месте, и расхождение видно на сборке, а не в браузере.
 */

export interface City {
  id: string;
  name: string;
  slug: string;
  timezone: string;
  /** Центр карты: среднее по заведениям города. */
  lat: number;
  lng: number;
  placesCount: number;
}

export type PointAvailability =
  | { open: true }
  | { open: false; reason: string; message: string; until: string | null };

/** Как гость может получить заказ в этом заведении. */
export interface Accepts {
  pickup: boolean;
  preorder: boolean;
  booking: boolean;
  delivery: boolean;
}

export type OrderType = "pickup" | "preorder" | "delivery" | "dine_in";

export interface Place {
  id: string;
  name: string;
  description: string;
  logoUrl: string | null;
  address: string;
  photos: string[];
  cuisines: string[];
  lat: number;
  lng: number;
  avgPrepMinutes: number;
  accepts: Accepts;
  /** Метры от гостя. Пусто, если он не дал геолокацию. */
  distanceM: number | null;
  availability: PointAvailability;
}

export interface ModifierOption {
  id: string;
  title: string;
  price: number;
  maxQty: number;
  exclusion: boolean;
}

export interface ModifierGroup {
  id: string;
  title: string;
  required: boolean;
  minSelect: number;
  maxSelect: number;
  options: ModifierOption[];
}

export interface MenuProduct {
  id: string;
  title: string;
  description: string;
  imageUrl: string | null;
  price: number;
  weightG: number | null;
  volumeMl: number | null;
  kind: "simple" | "combo";
  available: boolean;
  /** Состав набора (п. 6 ТЗ). Пуст у обычного блюда. */
  comboItems: { title: string; qty: number }[];
  modifierGroups: ModifierGroup[];
}

export interface MenuCategory {
  id: string;
  title: string;
  imageUrl: string | null;
  products: MenuProduct[];
}

export interface PointMenu {
  pointId: string;
  pointName: string;
  categories: MenuCategory[];
}

export interface SearchHit {
  productId: string;
  title: string;
  price: number;
  imageUrl: string | null;
  pointId: string;
  pointName: string;
}

export interface CartLineDto {
  productId: string;
  title: string;
  qty: number;
  unitPrice: number;
  /** Стоимость по обычным ценам, до акций. */
  baseTotal: number;
  discount: number;
  total: number;
  modifiers: { title: string; qty: number; total: number }[];
}

export interface PointCartDto {
  pointId: string;
  pointName: string;
  lines: CartLineDto[];
  total: number;
}

export interface CartTotalsDto {
  points: PointCartDto[];
  baseSubtotal: number;
  partnerDiscount: number;
  platformDiscount: number;
  total: number;
  promo: { id: string; code: string; discount: number } | null;
}

export type PartStatus =
  | "awaiting_payment"
  | "new"
  | "accepted"
  | "cooking"
  | "ready"
  | "delivering"
  | "delivered"
  | "handed"
  | "cancelled";

export interface OrderPartDto {
  number: string;
  point: { id: string; name: string };
  status: PartStatus;
  etaMinutes: number | null;
  total: number;
  lines: { title: string; qty: number; unitPrice: number; total: number }[];
  cancelReason: string | null;
}

export interface OrderDto {
  number: string;
  token: string;
  status: string;
  paymentStatus: string;
  type: OrderType;
  city: { id: string; name: string };
  customerName: string;
  comment: string;
  total: number;
  createdAt: string;
  paidAt: string | null;
  parts: OrderPartDto[];
}

export class ApiError extends Error {
  readonly code: string;
  readonly details: unknown;

  constructor(message: string, code: string, details: unknown) {
    super(message);
    this.name = "ApiError";
    this.code = code;
    this.details = details;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: init?.body
      ? { "Content-Type": "application/json", ...init.headers }
      : init?.headers
  });

  const body = await response.json().catch(() => ({}));

  if (!response.ok) {
    const error = body as { error?: string; code?: string; details?: unknown };
    throw new ApiError(
      error.error ?? "Не удалось выполнить запрос",
      error.code ?? "unknown",
      error.details
    );
  }

  return body as T;
}

/**
 * Случайный UUID там, где `crypto.randomUUID` недоступен.
 *
 * Он появился в браузерах только в 2021 году и работает лишь в защищённом
 * контексте. Телефон гостя — это что угодно, включая пятилетний Android с
 * чужим браузером, и терять на нём заказ нельзя.
 *
 * `getRandomValues` есть везде: ограничение защищённого контекста касается
 * `randomUUID` и `subtle`, но не его.
 */
function randomKey(): string {
  const source = typeof crypto === "undefined" ? undefined : crypto;

  if (typeof source?.randomUUID === "function") return source.randomUUID();

  if (typeof source?.getRandomValues === "function") {
    const bytes = source.getRandomValues(new Uint8Array(16));
    return Array.from(bytes, byte => byte.toString(16).padStart(2, "0")).join("");
  }

  /*
   * Совсем древний браузер. Ключ выходит предсказуемым, но от него требуется
   * не тайна, а неповторимость: он отличает повтор одной попытки от второй
   * операции. Отказать здесь значило бы не дать оформить заказ вовсе.
   */
  const chunk = () => Math.random().toString(16).slice(2, 10);
  return `${chunk()}${chunk()}${chunk()}${chunk()}`;
}

export interface StartedPayment {
  paymentId: string;
  /** Куда отправить гостя платить. Пусто — платить уже не надо. */
  confirmationUrl: string | null;
  status: string;
}

export interface PlacesFilter {
  q?: string;
  cuisine?: string;
  accepts?: "pickup" | "preorder" | "booking" | "delivery";
  near?: { lat: number; lng: number };
}

export const api = {
  cities: () => request<{ cities: City[] }>("/api/public/cities"),

  places: (cityId: string, filter: PlacesFilter = {}) => {
    const query = new URLSearchParams();
    if (filter.q) query.set("q", filter.q);
    if (filter.cuisine) query.set("cuisine", filter.cuisine);
    if (filter.accepts) query.set("accepts", filter.accepts);
    if (filter.near) {
      query.set("lat", String(filter.near.lat));
      query.set("lng", String(filter.near.lng));
    }

    return request<{ cityName: string; timezone: string; places: Place[] }>(
      `/api/public/cities/${cityId}/places?${query.toString()}`
    );
  },

  place: (pointId: string) => request<Place>(`/api/public/places/${pointId}`),

  pointMenu: (pointId: string) => request<PointMenu>(`/api/public/places/${pointId}/menu`),

  search: (cityId: string, query: string) =>
    request<{ results: SearchHit[] }>(
      `/api/public/cities/${cityId}/search?q=${encodeURIComponent(query)}`
    ),

  calculate: (cityId: string, items: unknown[], promoCode?: string) =>
    request<CartTotalsDto>("/api/public/cart/calculate", {
      method: "POST",
      body: JSON.stringify({ cityId, items, promoCode })
    }),

  /**
   * Оформление заказа.
   *
   * Ключ идемпотентности обязателен: гость жмёт кнопку на плохой связи и, не
   * дождавшись ответа, жмёт ещё раз. Сервер по ключу узнаёт повтор той же
   * попытки и возвращает тот же заказ вместо второго.
   */
  createOrder: (payload: unknown, key: string) =>
    request<OrderDto>("/api/public/orders", {
      method: "POST",
      body: JSON.stringify(payload),
      headers: { "Idempotency-Key": key }
    }),

  /**
   * Начало оплаты.
   *
   * Возвращает адрес платёжной страницы провайдера. Ключ идемпотентности
   * обязателен по той же причине, что и при оформлении: повторное нажатие на
   * плохой связи не должно заводить второй платёж.
   */
  startPayment: (token: string, key: string) =>
    request<StartedPayment>(`/api/public/orders/${token}/pay`, {
      method: "POST",
      headers: { "Idempotency-Key": key }
    }),

  order: (token: string) => request<OrderDto>(`/api/public/orders/${token}`),

  newKey: randomKey
};
